/**
 * `GeographySection` — country breakdown for the cross-account
 * metrics dashboard, backed by `AdminMetricsOverview.geography`.
 *
 * Anatomy:
 *   - One row per country, ordered by `active_accounts` DESC (server-
 *     sorted; we render in array order).
 *   - The bar represents `active_accounts` normalised to the max in
 *     the window — the eye reads relative size, the numeric column to
 *     the right gives the exact count + records sub-stat.
 *   - The literal `'ZZ'` "unknown" bucket renders explicitly as
 *     "Unknown" with a globe icon (not a flag) so a staff member
 *     never mistakes it for a real country code.
 *
 * Geography is NON-RETROACTIVE (the `geo_country` was added to
 * `discovery_context.edge_meta` mid-flight; pre-instrumentation
 * audit records carry no country). Until enough new traffic accrues,
 * "Unknown" can dominate or be the only row. We surface that as a
 * calm caption — NOT an error — so the dashboard doesn't look broken
 * during the bootstrap window.
 *
 * Loading / error states share the surrounding section frame so a
 * partial overview failure doesn't blank the page.
 */

import { Globe2 } from 'lucide-react';
import { useMemo } from 'react';

import type { AdminMetricsGeographyRow } from '@sill/types';

import { Card } from '../../../components/atoms/Card.js';
import { countryFlagEmoji, countryName } from '../../../lib/country-names.js';
import { formatCount } from '../../../lib/format.js';
import { useC } from '../../../lib/theme.js';
import { SectionHeader } from '../KpiTile.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export type GeographySectionState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; geography: ReadonlyArray<AdminMetricsGeographyRow> };

export interface GeographySectionProps {
  state: GeographySectionState;
}

export function GeographySection({ state }: GeographySectionProps): JSX.Element {
  return (
    <section>
      <SectionHeader
        kicker="By geography"
        title="Agent traffic by country"
        caption="Where agent requests originate — not where accounts signed up. Records is the lead metric (the trustworthy volume signal); distinct active accounts is shown as the secondary stat. Country accrues from new agent traffic; historical traffic shows as Unknown."
      />
      <GeographyBody state={state} />
    </section>
  );
}

function GeographyBody({ state }: { state: GeographySectionState }): JSX.Element {
  const C = useC();

  if (state.kind === 'loading') {
    return (
      <Card style={{ padding: 24 }}>
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.dim,
            letterSpacing: 1.5,
          }}
        >
          Loading geography…
        </span>
      </Card>
    );
  }

  if (state.kind === 'error') {
    return (
      <Card style={{ padding: 18 }}>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.coralFg,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          Geography unavailable
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.body }}>
          {state.message}
        </div>
      </Card>
    );
  }

  return <GeographyList rows={state.geography} />;
}

function GeographyList({
  rows,
}: {
  rows: ReadonlyArray<AdminMetricsGeographyRow>;
}): JSX.Element {
  const C = useC();

  // Pre-derive all aggregates BEFORE any conditional return so hook
  // order is stable on re-renders. (`rows` is the single dep — every
  // derived value reduces over it.) Records is the LEAD metric — it's
  // the trustworthy volume signal (every audit_record carries country
  // once instrumentation lands). `active_accounts` is the secondary
  // stat (server-side distinct count).
  const derived = useMemo(() => {
    const maxRecords = rows.reduce((m, r) => Math.max(m, r.records), 0);
    const totalRecords = rows.reduce((sum, r) => sum + r.records, 0);
    const unknownRecords =
      rows.find((r) => r.geo_country === 'ZZ')?.records ?? 0;
    const unknownShare = totalRecords === 0 ? 0 : unknownRecords / totalRecords;
    const onlyUnknown = rows.length === 1 && rows[0]?.geo_country === 'ZZ';
    return { maxRecords, totalRecords, unknownShare, onlyUnknown };
  }, [rows]);

  // Server guarantees `'ZZ'` is always present; if the array is empty
  // the rollup hasn't produced ANY row yet (cold start) — render the
  // honest empty state.
  if (rows.length === 0) {
    return (
      <Card style={{ padding: 24 }}>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          No geography yet
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.body,
            lineHeight: 1.5,
          }}
        >
          Country breakdown accrues as new agent traffic lands on the edge.
        </div>
      </Card>
    );
  }

  const { maxRecords, totalRecords, unknownShare, onlyUnknown } = derived;

  return (
    <Card style={{ padding: 18 }}>
      {onlyUnknown || unknownShare >= 0.95 ? (
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 11,
            color: C.muted,
            lineHeight: 1.5,
            marginBottom: 14,
            padding: '8px 10px',
            background: C.surface2,
            border: `1px solid ${C.borderSoft}`,
            borderRadius: 2,
          }}
        >
          Geography is bootstrapping — most traffic in this window predates
          country instrumentation and lands in the Unknown bucket. New
          agent traffic will populate per-country rows from here on.
        </div>
      ) : null}

      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {rows.map((row) => (
          <GeographyRow
            key={row.geo_country}
            row={row}
            maxRecords={maxRecords}
            totalRecords={totalRecords}
          />
        ))}
      </ul>
    </Card>
  );
}

function GeographyRow({
  row,
  maxRecords,
  totalRecords,
}: {
  row: AdminMetricsGeographyRow;
  maxRecords: number;
  totalRecords: number;
}): JSX.Element {
  const C = useC();

  const isUnknown = row.geo_country === 'ZZ';
  const label = countryName(row.geo_country);
  const flag = isUnknown ? '' : countryFlagEmoji(row.geo_country);

  // Bar tracks `records` — the trustworthy volume signal. `active_accounts`
  // is now the secondary sub-stat.
  const widthPct =
    maxRecords === 0
      ? 0
      : Math.max(0, Math.min(1, row.records / maxRecords)) * 100;

  const recordShare =
    totalRecords === 0 ? null : row.records / totalRecords;

  // Unknown bucket renders dimmer (muted bar) so the eye still parses
  // the country rows as the primary signal. Known countries use the
  // semantic accent (`sky` reads as "where", not "good/bad" — keeps
  // lime reserved for retention/approval and coral for failure).
  const barColor = isUnknown ? C.dim : C.skyFg;
  const labelColor = isUnknown ? C.muted : C.text;

  return (
    <li>
      <div className="flex items-center" style={{ gap: 12 }}>
        {/* Flag / globe glyph */}
        <div
          aria-hidden="true"
          style={{
            width: 26,
            flexShrink: 0,
            fontSize: 16,
            lineHeight: 1,
            textAlign: 'center',
            color: C.muted,
          }}
        >
          {flag !== '' ? (
            flag
          ) : (
            <Globe2 size={14} color={C.muted} style={{ verticalAlign: 'middle' }} />
          )}
        </div>

        {/* Country label + ISO code */}
        <div
          style={{
            minWidth: 168,
            flexShrink: 0,
            fontFamily: sansFont,
            fontSize: 13,
            color: labelColor,
            lineHeight: 1.3,
            display: 'flex',
            alignItems: 'baseline',
            gap: 8,
          }}
        >
          <span style={{ fontWeight: 500 }}>{label}</span>
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.dim,
              letterSpacing: 1,
              textTransform: 'uppercase',
            }}
          >
            {row.geo_country}
          </span>
        </div>

        {/* Bar */}
        <div
          style={{
            flex: 1,
            minWidth: 60,
            height: 8,
            background: C.surface2,
            border: `1px solid ${C.borderSoft}`,
            borderRadius: 1,
            overflow: 'hidden',
          }}
          aria-hidden="true"
        >
          <div
            style={{
              height: '100%',
              width: `${widthPct.toFixed(1)}%`,
              background: barColor,
              opacity: isUnknown ? 0.55 : 0.85,
              transition: 'width 200ms ease-out',
            }}
          />
        </div>

        {/* Numeric column — records (LEAD metric: agent-request volume) */}
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 13,
            color: C.text,
            fontWeight: 500,
            letterSpacing: -0.2,
            minWidth: 72,
            textAlign: 'right',
          }}
          aria-label={
            recordShare === null
              ? `${formatCount(row.records)} records`
              : `${formatCount(row.records)} records · ${(recordShare * 100).toFixed(1)}% of window`
          }
          title={
            recordShare === null
              ? `${formatCount(row.records)} records`
              : `${formatCount(row.records)} records · ${(recordShare * 100).toFixed(1)}% of window`
          }
        >
          {formatCount(row.records)}
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.dim,
              marginLeft: 4,
              letterSpacing: 0.8,
              textTransform: 'uppercase',
            }}
          >
            rec
          </span>
        </div>

        {/* Active accounts sub-stat — distinct account count for the country */}
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 0.3,
            minWidth: 96,
            textAlign: 'right',
          }}
          title={`${formatCount(row.active_accounts)} distinct active accounts`}
        >
          {formatCount(row.active_accounts)} accts
        </div>
      </div>
    </li>
  );
}
